import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import ClockWidget from "@/components/ClockWidget";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Link } from "react-router-dom";
import { fmtDateTime, fmtTime } from "@/lib/format";
import { Users, ListChecks, MessageSquareWarning, AlarmClock, Megaphone, CalendarCheck } from "lucide-react";

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="card-emerald p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
        <Icon style={{ width: 18, height: 18 }} className="text-emerald-400" />
      </div>
      <p className="mt-3 font-display text-3xl font-extrabold">{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isMgr = ["manager", "admin", "hr", "super_admin"].includes(user?.role);
  const isSuper = user?.role === "super_admin";
  const [data, setData] = useState({ tasks: [], attendance: [], feedback: [], employees: [], broadcasts: [], leave: [], changeRequests: [], activity: [] });

  const load = async () => {
    const base = [api.get("/tasks"), api.get("/broadcasts"), api.get("/feedback")];
    if (isMgr) {
      const reqs = [...base, api.get("/employees"), api.get("/attendance", { params: { status: "exceptions" } }), api.get("/leave"), api.get("/attendance")];
      if (isSuper) reqs.push(api.get("/change-requests"));
      const res = await Promise.allSettled(reqs);
      const g = (i) => (res[i]?.status === "fulfilled" ? res[i].value.data : []);
      setData({ tasks: g(0), broadcasts: g(1), feedback: g(2), employees: g(3), attendance: g(4), leave: g(5), activity: g(6), changeRequests: isSuper ? g(7) : [] });
    } else {
      const res = await Promise.allSettled([...base, api.get("/attendance/me")]);
      const g = (i) => (res[i]?.status === "fulfilled" ? res[i].value.data : []);
      setData({ tasks: g(0), broadcasts: g(1), feedback: g(2), employees: [], attendance: g(3), leave: [], activity: [], changeRequests: [] });
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const pendingExceptions = isMgr ? data.attendance : [];
  const pendingLeave = isMgr ? data.leave.filter((l) => l.status === "pending") : [];
  const pendingChangeRequests = isSuper ? data.changeRequests.filter((c) => c.status === "pending") : [];
  const openFeedback = data.feedback.filter((f) => f.status !== "resolved");
  const myTasks = data.tasks.filter((t) => t.status !== "done");
  const daysLogged = new Set(data.attendance.map((a) => a.date)).size;
  const ts = (a) => a.clock_out_recorded_at || a.clock_in_recorded_at || a.created_at || "";
  const recentActivity = isMgr ? [...data.activity].sort((x, y) => ts(y).localeCompare(ts(x))).slice(0, 8) : [];

  return (
    <div className="space-y-6" data-testid="dashboard">
      {isMgr && (pendingExceptions.length > 0 || pendingLeave.length > 0 || pendingChangeRequests.length > 0) && (
        <div className="card-emerald p-6 border-amber-500/25" data-testid="action-center">
          <div className="flex items-center gap-2 mb-4">
            <AlarmClock className="w-5 h-5 text-amber-400" />
            <h3 className="font-display text-lg font-semibold">Action Required</h3>
            <span className="text-xs text-muted-foreground">What needs your attention today</span>
          </div>
          <div className="space-y-2.5">
            {pendingExceptions.slice(0, 4).map((a) => {
              const early = a.early_out_request?.status === "pending";
              return (
                <Link key={a.id} to="/app/attendance" className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" data-testid="action-item">
                  <div>
                    <p className="text-sm font-medium">{a.user_name} · {early ? "Early clock-out request" : "Clock-out review"}</p>
                    <p className="text-xs text-muted-foreground">{a.date}{a.early_out_request?.reason ? ` — "${a.early_out_request.reason}"` : ""}</p>
                  </div>
                  <Badge className="bg-amber-500/20 text-amber-300">{early ? "Approve early out" : "Review"}</Badge>
                </Link>
              );
            })}
            {pendingLeave.slice(0, 4).map((l) => (
              <Link key={l.id} to="/app/leave" className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" data-testid="action-item">
                <div>
                  <p className="text-sm font-medium">{l.name} · {l.type} leave</p>
                  <p className="text-xs text-muted-foreground">{l.start_date} → {l.end_date} ({l.days}d)</p>
                </div>
                <Badge className="bg-sky-500/20 text-sky-300">Approve leave</Badge>
              </Link>
            ))}
            {pendingChangeRequests.slice(0, 4).map((c) => (
              <Link key={c.id} to="/app/workplaces" className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" data-testid="action-item">
                <div>
                  <p className="text-sm font-medium">{c.requester_name} · workplace change</p>
                  <p className="text-xs text-muted-foreground">{c.target_name} — {Object.keys(c.changes || {}).join(", ")}</p>
                </div>
                <Badge className="bg-purple-500/20 text-purple-300">Review request</Badge>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1 min-w-0"><ClockWidget onChange={load} /></div>
        <div className="grid gap-4 sm:grid-cols-2 lg:col-span-2 content-start">
          {isMgr ? (
            <>
              <Stat icon={Users} label="Team members" value={data.employees.length} testid="stat-employees" />
              <Stat icon={AlarmClock} label="Pending approvals" value={pendingExceptions.length} testid="stat-approvals" />
              <Stat icon={MessageSquareWarning} label="Open feedback" value={openFeedback.length} testid="stat-feedback" />
              <Stat icon={ListChecks} label="Active tasks" value={myTasks.length} testid="stat-tasks" />
            </>
          ) : (
            <>
              <Stat icon={ListChecks} label="My open tasks" value={myTasks.length} testid="stat-tasks" />
              <Stat icon={CalendarCheck} label="Days logged" value={daysLogged} testid="stat-days" />
              <Stat icon={MessageSquareWarning} label="My submissions" value={data.feedback.length} testid="stat-feedback" />
              <Stat icon={AlarmClock} label="Pending exceptions" value={data.attendance.filter((a) => a.exception_status === "pending").length} testid="stat-exceptions" />
            </>
          )}
        </div>
      </div>

      {isMgr && (
        <div className="card-emerald p-6" data-testid="attendance-activity">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold">Clock-in / out activity</h3>
            <Link to="/app/attendance" className="text-sm text-emerald-400 hover:underline">View all</Link>
          </div>
          {recentActivity.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No clock activity yet.</p>
          ) : (
            <div className="divide-y divide-white/5">
              {recentActivity.map((a) => (
                <div key={a.id} className="flex items-center justify-between py-2.5 gap-3" data-testid="activity-row">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{a.user_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{a.date}{a.clock_in_workplace ? ` · ${a.clock_in_workplace}` : ""}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs">
                      <span className="text-emerald-300">In</span> <span className="font-mono">{fmtTime(a.clock_in)}</span>
                      {"  "}<span className={a.clock_out ? "text-sky-300" : "text-muted-foreground"}>Out</span> <span className="font-mono">{a.clock_out ? fmtTime(a.clock_out) : "—"}</span>
                    </p>
                    <p className="text-[10px] text-muted-foreground capitalize">{((a.clock_out ? a.clock_out_method : a.clock_in_method) || "manual").replace("_", " ")}{!a.clock_out ? " · active" : ""}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 card-emerald p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold">{isMgr ? "Clock-out exceptions" : "My tasks"}</h3>
            <Link to={isMgr ? "/app/attendance" : "/app/tasks"} className="text-sm text-emerald-400 hover:underline">View all</Link>
          </div>
          {isMgr ? (
            pendingExceptions.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No pending approvals.</p>
            ) : (
              <div className="space-y-3">
                {pendingExceptions.slice(0, 5).map((a) => (
                  <div key={a.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5" data-testid="pending-exception">
                    <div>
                      <p className="text-sm font-medium">{a.user_name} · {a.date}</p>
                      <p className="text-xs text-muted-foreground">{a.exception_reason}</p>
                    </div>
                    <Badge className="bg-amber-500/20 text-amber-300">Pending</Badge>
                  </div>
                ))}
              </div>
            )
          ) : (
            myTasks.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No open tasks. Nice work!</p>
            ) : (
              <div className="space-y-4">
                {myTasks.slice(0, 6).map((t) => (
                  <div key={t.id}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{t.title}</span>
                      <span className="text-muted-foreground">{t.progress}%</span>
                    </div>
                    <Progress value={t.progress} className="mt-2 h-2 bg-white/5" />
                  </div>
                ))}
              </div>
            )
          )}
        </div>

        <div className="card-emerald p-6">
          <div className="flex items-center gap-2 mb-4">
            <Megaphone className="w-5 h-5 text-emerald-400" />
            <h3 className="font-display text-lg font-semibold">Announcements</h3>
          </div>
          {data.broadcasts.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No announcements yet.</p>
          ) : (
            <div className="space-y-4">
              {data.broadcasts.slice(0, 4).map((b) => (
                <div key={b.id} className="border-l-2 border-emerald-500/40 pl-3">
                  <p className="text-sm font-medium">{b.title}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2">{b.message}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">{fmtDateTime(b.created_at)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
